import { User } from "firebase/auth";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchPlayers, Player } from "../../../api/players";
import { card, inputStyle, smallBtn, tableStyle, thStyle, tdStyle } from "../../../styles";

export const TARGET_PLAYER_SLOTS = 4;

interface InitialPlayer {
  id: string;
  name: string;
  position: string | null;
  club: string | null;
}

interface Props {
  user: User;
  competitionId: string;
  selectedIds: string[];
  onChange: (playerIds: string[]) => void;
  /** Prefills the "already selected" chips with real names/clubs when editing an existing
   * member's squad — omitted entirely for the add-member backfill flow, which starts empty. */
  initialPlayers?: InitialPlayer[];
}

/**
 * Admin-only squad picker for backfilling a TARGET member's selections after their
 * deadline has passed, or editing an existing member's squad mid-season — the same
 * fetchPlayers wrapper TeamPage's own player pool uses (frontend/src/api/players.ts),
 * simplified to a standalone component since this form has no page-level "own team vs
 * guest team" state to share.
 */
export default function TargetSquadPicker({ user, competitionId, selectedIds, onChange, initialPlayers }: Props) {
  const { t } = useTranslation();
  const [players, setPlayers] = useState<Player[]>([]);
  const [selectedPlayers, setSelectedPlayers] = useState<Record<string, Player>>(() => {
    const seeded: Record<string, Player> = {};
    for (const ip of initialPlayers ?? []) {
      seeded[ip.id] = { id: ip.id, name: ip.name, club: ip.club ?? "", position: ip.position, nationality: null, squadNumber: null, dateOfBirth: null, pictureUrl: null };
    }
    return seeded;
  });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const pageSize = 10;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchPlayers(user, { competitionId, page, pageSize, search: search.trim() || undefined })
      .then((res) => {
        if (cancelled) return;
        setPlayers(res.players);
        setTotal(res.total);
      })
      .catch(() => { /* ignore — picker just shows an empty list */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user, competitionId, search, page]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const addPlayer = (player: Player) => {
    if (selectedIds.length >= TARGET_PLAYER_SLOTS || selectedIds.includes(player.id)) return;
    setSelectedPlayers((prev) => ({ ...prev, [player.id]: player }));
    onChange([...selectedIds, player.id]);
  };

  const removePlayer = (playerId: string) => {
    onChange(selectedIds.filter((id) => id !== playerId));
  };

  return (
    <div style={{ ...card, padding: 0, overflow: "hidden" }}>
      <div style={{ padding: "0.9rem 1.1rem 0.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
        <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>
          {t("admin.leagues.detail.backfill.target.selectedCount", { count: selectedIds.length, slots: TARGET_PLAYER_SLOTS })}
        </span>
        <input
          style={{ ...inputStyle, fontSize: "0.85rem", width: 180 }}
          placeholder={t("team.playerPicker.search") as string}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
      </div>

      {selectedIds.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", padding: "0 1.1rem 0.7rem" }}>
          {selectedIds.map((id) => {
            const player = selectedPlayers[id];
            return (
              <span
                key={id}
                style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", padding: "3px 8px", borderRadius: 99, background: "var(--color-accent-glow)", border: "1px solid var(--color-accent)", fontSize: "0.75rem", fontWeight: 600 }}
              >
                {player?.name ?? id}
                <button
                  onClick={() => removePlayer(id)}
                  aria-label={t("admin.leagues.detail.backfill.removePlayer", { name: player?.name ?? id }) as string}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-danger)", padding: 0, fontSize: "0.8rem", lineHeight: 1 }}
                >
                  ✕
                </button>
              </span>
            );
          })}
        </div>
      )}

      <table style={{ ...tableStyle, opacity: loading ? 0.5 : 1 }}>
        <thead>
          <tr>
            <th style={thStyle}>{t("team.playerPicker.columns.name")}</th>
            <th style={thStyle}>{t("team.playerPicker.columns.club")}</th>
            <th style={thStyle}>{t("team.playerPicker.columns.position")}</th>
            <th style={{ ...thStyle, width: 80 }} />
          </tr>
        </thead>
        <tbody>
          {players.map((p) => {
            const sel = selectedIds.includes(p.id);
            const full = !sel && selectedIds.length >= TARGET_PLAYER_SLOTS;
            return (
              <tr key={p.id} style={{ background: sel ? "var(--color-accent-glow)" : undefined }}>
                <td style={tdStyle}>{p.name}</td>
                <td style={tdStyle}>{p.club}</td>
                <td style={tdStyle}>{p.position ?? "—"}</td>
                <td style={{ ...tdStyle, textAlign: "right" }}>
                  {sel ? (
                    <button onClick={() => removePlayer(p.id)} style={{ ...smallBtn, fontSize: "0.72rem", color: "var(--color-danger)", border: "1px solid var(--color-danger)" }}>
                      {t("admin.leagues.detail.backfill.remove")}
                    </button>
                  ) : (
                    <button onClick={() => addPlayer(p)} disabled={full} style={{ ...smallBtn, fontSize: "0.72rem", opacity: full ? 0.4 : 1, cursor: full ? "not-allowed" : "pointer" }}>
                      {t("admin.leagues.detail.backfill.add")}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
          {players.length === 0 && !loading && (
            <tr><td style={tdStyle} colSpan={4}>{t("admin.leagues.detail.backfill.noResults")}</td></tr>
          )}
        </tbody>
      </table>

      {totalPages > 1 && (
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", padding: "0.6rem 1.1rem", borderTop: "1px solid var(--color-border)" }}>
          <button style={smallBtn} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>{t("admin.pagination.previous")}</button>
          <span style={{ fontSize: "0.8rem", color: "var(--color-text-muted)" }}>{t("admin.pagination.pageOf", { page, total: totalPages })}</span>
          <button style={smallBtn} disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>{t("admin.pagination.next")}</button>
        </div>
      )}
    </div>
  );
}
